import { message, Table, Tag } from "antd"
import React, { FC, memo, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { ColumnProps } from "antd/lib/table"
import {
    BookerInput,
    BookerPaymentMethodInput,
    GetCompanySettingsPaymentmethodsQuery,
    GetUpdatedBookerDocument,
    GetUpdatedBookerQuery,
    Maybe,
    PaymentMethod,
    PaymentTransferType,
    useUpdateBookerMutation,
} from "../../generated/graphql"
import PaymentMethodBadge from "../PaymentMethodBadge/PaymentMethodBadge"
import css from "./PaymentMethodTable.module.css"
import { getPaymentMethodType, PaymentMethodData, PaymentMethodType } from "../../helpers/getPaymentMethodType"
import { isPaymentMethodUsableForBooking } from "../../helpers/isPaymentMethodUsableForBooking"
import { Key } from "antd/lib/table/interface"
import SepaDebitPaymentMethodModal, { ModalDataType } from "../../containers/CreateBooking/Steps/paymentMethodForms/SepaDebitPaymentMethodModal"
import { omit } from "../../helpers/omit"
import { v4 } from "uuid"
import { CompanyMethodsWithIds } from "../../containers/CreateBooking/Steps/StepSummary/SummaryRight"
import { red } from "@ant-design/colors"

import creditcardIcon from "../../assets/payment-icons/mollie/creditcard.svg"
import paypalIcon from "../../assets/payment-icons/mollie/paypal.svg"
import banktransferIcon from "../../assets/payment-icons/mollie/banktransfer.svg"
import directdebitIcon from "../../assets/payment-icons/mollie/directdebit.svg"
import applepayIcon from "../../assets/payment-icons/mollie/applepay.svg"
import googlepayIcon from "../../assets/payment-icons/mollie/googlepay.svg"

/** Lokale SVG-Icons für Mollie-Methoden */
const MOLLIE_ICON_MAP: Record<string, string> = {
    creditcard: creditcardIcon,
    paypal: paypalIcon,
    banktransfer: banktransferIcon,
    directdebit: directdebitIcon,
    applepay: applepayIcon,
    googlepay: googlepayIcon,
}

/** Mapping: Mollie-Methode → Standard-Methode die sie ersetzt */
const MOLLIE_TO_STANDARD_OVERLAP: Record<string, PaymentMethodType> = {
    banktransfer: "bankTransfer",
    directdebit: "sepaDebit",
}

/** Beschriftungen für Mollie-Methoden */
const MOLLIE_METHOD_LABELS: Record<string, string> = {
    creditcard: "Kreditkarte",
    paypal: "PayPal",
    banktransfer: "Überweisung",
    directdebit: "Lastschrift",
    applepay: "Apple Pay",
    googlepay: "Google Pay",
}

export type MollieMethodRow = {
    id: string
    description: string
    imageUrl?: string | null
}

type PaymentMethodTableProps = {
    paymentMethods: Array<Partial<PaymentMethod>>
    paymentMethodId: Maybe<string>
    /**
     * Meldet die gewählte Zahlart nach oben. Die Shape `{ id }` ist verbindlich —
     * `SummaryRight.handlePamentMethodChange` liest `e.id`. Ein String-Aufruf hat
     * produktiv einen stale `paymentMethodId` im Booking-State hinterlassen (Issue #113),
     * deshalb ist die Prop bewusst eng typisiert statt `Function`.
     */
    setSelectedPayment: (paymentMethod: { id: string }) => void
    showHeader?: boolean
    booker: GetUpdatedBookerQuery["booker"]
    companyMethodsWithIds: Array<CompanyMethodsWithIds>
    liftSepaDebitData: Function
    mollieMethodSelected?: boolean
    /** Mollie-Zahlungsmethoden zur Anzeige in der vereinheitlichten Liste */
    mollieMethods?: Array<MollieMethodRow>
    /** Aktuell ausgewählte Mollie-Methode (ID) */
    selectedMollieMethod?: string | null
    /** Callback wenn eine Mollie-Methode gewählt wird */
    onMollieMethodSelect?: (methodId: string | null) => void
    /** Erst nach einem Submit-Versuch die fehlende Auswahl rot hervorheben (F-005) */
    showAlert?: boolean
}

/** Unterscheidung ob eine Zeile aus Mollie oder dem Standard-System kommt */
type RowSource = "mollie" | "standard"

type CombinedTableData = Partial<PaymentMethod> & { id: string; _source?: RowSource; _mollieLabel?: string }

export type CompanyMethods = GetCompanySettingsPaymentmethodsQuery["settings"]

// type PaymentMethodTableData = { method: Extract<keyof CompanyMethods, PaymentMethodType>; active: boolean }

const initialPaymentMethodId = v4() as PaymentMethod["id"]

const PaymentMethodTable: FC<PaymentMethodTableProps> = ({
    paymentMethods,
    paymentMethodId,
    setSelectedPayment,
    showHeader = true,
    booker,
    companyMethodsWithIds,
    liftSepaDebitData,
    mollieMethodSelected = false,
    mollieMethods = [],
    selectedMollieMethod = null,
    onMollieMethodSelect,
    showAlert = false,
}) => {
    const { t } = useTranslation()
    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<Key>>([])
    const [availablePaymentMethodWithIds, setAvailablePaymentMethodWithIds] = useState<Array<CompanyMethodsWithIds>>(companyMethodsWithIds)
    const [newPaymentMethodId, setNewPaymentMethodId] = useState<PaymentMethod["id"]>(initialPaymentMethodId)
    const [updateBookerDb, { loading: updatingBooker }] = useUpdateBookerMutation()

    useEffect(() => {
        if (companyMethodsWithIds.length > 0) {
            const methods: Array<CompanyMethodsWithIds> = []
            companyMethodsWithIds.forEach((method) => {
                if (method.active && method.method !== "id") {
                    methods.push({ method: method.method, active: method.active, id: method.id })
                }
            })
            setAvailablePaymentMethodWithIds(methods)
        }
    }, [companyMethodsWithIds])

    // Wenn von außen eine Mollie-Methode gewählt wird, die Zeile in der Tabelle selektieren
    useEffect(() => {
        if (selectedMollieMethod) {
            setSelectedRowKeys([selectedMollieMethod])
        } else if (mollieMethodSelected) {
            // Fallback: alte Logik falls mollieMethodSelected ohne selectedMollieMethod gesetzt wird
            setSelectedRowKeys([])
        }
    }, [selectedMollieMethod, mollieMethodSelected])

    useEffect(() => {
        // Wenn eine Mollie-Methode aktiv ist, Standard-Selektion nicht überschreiben
        if (selectedMollieMethod) return

        if (paymentMethodId) {
            const selectedPaymentMethod = paymentMethods.filter((method) => method.id === paymentMethodId)
            if (selectedPaymentMethod[0]) {
                const selectedPaymentMethodType = getPaymentMethodType(selectedPaymentMethod[0])
                const otherPaymentMethods = paymentMethods.filter((method) => getPaymentMethodType(method) !== "none")
                if (selectedPaymentMethodType === "none" && otherPaymentMethods[0]?.id) {
                    setSelectedRowKeys([otherPaymentMethods[0].id])
                    committedSelectionRef.current = { keys: [otherPaymentMethods[0].id], rows: [{ ...otherPaymentMethods[0], id: otherPaymentMethods[0].id! }] }
                    // Callback in SummaryRight erwartet `{ id: string }`, kein Array — sonst bleibt
                    // paymentMethodId leer und der Submit-Button bleibt stumm deaktiviert.
                    setSelectedPayment({ id: otherPaymentMethods[0].id! })
                } else {
                    setSelectedRowKeys([paymentMethodId])
                    // Issue #115: die initial persistierte Auswahl ist der Rollback-Anker, sonst
                    // rollt der Fehlerpfad in rowSelection.onChange beim ERSTEN Klick ins Leere
                    // (setSelectedPayment({id: ""}) + gesperrtes Submit-Gate).
                    committedSelectionRef.current = { keys: [paymentMethodId], rows: [{ ...selectedPaymentMethod[0], id: paymentMethodId }] }
                    // B-82808: Die Submit-Sperre hängt an der GEWÄHLTEN Methode, nicht daran, ob
                    // irgendeine Methode des Buchers eine IBAN trägt.
                    liftSepaDebitData(isPaymentMethodUsableForBooking(selectedPaymentMethod[0]))
                }
            }
        }
    }, [liftSepaDebitData, paymentMethodId, paymentMethods, setSelectedPayment, selectedMollieMethod])

    // Issue #113: Hier stand ein Effekt, der `selectedRowKeys[0]` bei jeder Änderung
    // als STRING an setSelectedPayment gab. Der Callback liest aber `e.id` — der Aufruf
    // war für den Booking-State also immer ein No-op und konnte den Rollback im
    // Fehlerpfad (unten) nicht erledigen. Er wurde ersatzlos entfernt statt „korrigiert“:
    // mit der richtigen Shape hätte er eine Render-Schleife erzeugt, weil
    // updateBookingData ein neues bookingData-Objekt liefert, dadurch die inline
    // definierte setSelectedPayment-Prop neu entsteht und der Sync-Effekt oben
    // (paymentMethodId → selectedRowKeys) erneut ein neues Array setzt.
    // Alle Aufrufer melden ihre Auswahl jetzt explizit: onChange im Erfolgsfall,
    // der .catch-Zweig beim Rollback.

    // Spiegelt die aktuell gewählte Mollie-Methode für die Closures im Fehlerpfad:
    // wählt der User während der laufenden Mutation eine Mollie-Methode, darf der
    // Rollback dessen neue Auswahl nicht überschreiben.
    const selectedMollieMethodRef = useRef(selectedMollieMethod)
    // Issue #116: laufende Nummer der Standard-Auswahl. Der Fehlerpfad einer älteren
    // Persistenz darf eine inzwischen neuere Auswahl nicht zurückrollen (latest wins).
    const selectionSeqRef = useRef(0)
    // Issue #116: zuletzt ERFOLGREICH persistierte (oder initial geladene) Auswahl. Der
    // Rollback stellt diese her — nicht die Auswahl unmittelbar vor dem Klick, die selbst
    // eine nie gespeicherte Methode sein kann (A scheitert, B scheitert → nicht auf A).
    const committedSelectionRef = useRef<{ keys: Array<Key>; rows: Array<CombinedTableData> }>({ keys: [], rows: [] })
    useEffect(() => {
        selectedMollieMethodRef.current = selectedMollieMethod
    }, [selectedMollieMethod])

    const createPaymentMethod = async (newPaymentMethodData: Partial<PaymentMethod>) => {
        try {
            const currentBookerData = { ...booker } // Create a shallow copy
            // "attendees" bleibt weg: die Liste aus dem Cache kann veraltet sein (ko-X-main #1491), und eine Zahlart ändert keinen Teilnehmer.
            const omitedEntries: Array<Extract<keyof GetUpdatedBookerQuery["booker"], string>> = ["createDate", "updateDate", "customerId", "addition", "annotation", "updateDate", "createdBy", "attendees"]
            const updateBookerData = omit(["bookerId", ...omitedEntries], currentBookerData)

            // Ensure customer ID is properly set
            updateBookerData.customer = { id: currentBookerData.customerId }

            const newPaymentMethodType = getPaymentMethodType(newPaymentMethodData)
            const existingPaymentMethodTypes = currentBookerData.paymentMethods?.map((paymentMethod) => getPaymentMethodType(paymentMethod))

            if (existingPaymentMethodTypes?.includes(newPaymentMethodType as PaymentMethodType)) {
                // Zahlungsmethode existiert bereits, kein erneutes Anlegen nötig
            } else {
                // Lokale UI-Metadaten entfernen, bevor die Payment-Method an UpdateBooker geschickt wird.
                // Diese Felder (_source, _mollieLabel, active) sind nicht Teil von BookerPaymentMethodInput
                // und würden sonst einen 400 Bad Request auslösen.
                const cleanedPaymentMethod = omit(["_source", "_mollieLabel", "active", "__typename"], newPaymentMethodData)

                // Ensure paymentMethods is an array
                updateBookerData.paymentMethods = [...(updateBookerData.paymentMethods || [])]
                updateBookerData.paymentMethods.push(cleanedPaymentMethod)

                await updateBookerDb({
                    variables: {
                        updateBookerData,
                        fromEndUserApp: true,
                    },
                    refetchQueries: [{ query: GetUpdatedBookerDocument, variables: { skip: !booker?.id, id: booker?.id ?? "" } }],
                })
            }
        } catch (error) {
            throw error
        }
    }

    const updateSepaDebitData = async (newPaymentMethodData: BookerPaymentMethodInput | ModalDataType) => {
        try {
            // Erstelle eine bereinigte Kopie der Booker-Daten
            // const currentBookerData = cleanupGraphQLData(booker)
            const currentBookerData = booker

            const omitedEntries: Array<Extract<keyof GetUpdatedBookerQuery["booker"], string>> = ["createDate", "updateDate", "customerId", "addition", "annotation", "updateDate", "createdBy", "attendees"]

            const updateBookerData: BookerInput = omit(["bookerId", ...omitedEntries], currentBookerData)

            // Ensure customer ID is properly set
            updateBookerData.customer = { id: currentBookerData.customerId }

            // Update or add new payment method
            const existingPaymentIds: string[] = updateBookerData.paymentMethods?.map((pm: BookerPaymentMethodInput) => pm.id) || []

            if (existingPaymentIds.includes(newPaymentMethodData.id)) {
                updateBookerData.paymentMethods = updateBookerData.paymentMethods?.map((pm: BookerPaymentMethodInput) => (pm.id === newPaymentMethodData.id ? { ...pm, ...newPaymentMethodData } : pm))
            } else {
                updateBookerData.paymentMethods = [...(updateBookerData.paymentMethods || []), newPaymentMethodData]
            }

            const result = await updateBookerDb({
                variables: {
                    updateBookerData,
                    fromEndUserApp: true,
                },
                refetchQueries: [
                    {
                        query: GetUpdatedBookerDocument,
                        variables: {
                            skip: !booker.id,
                            id: booker.id ?? "",
                        },
                    },
                ],
            })

            if (result.data) {
                if (!updatingBooker) {
                    setNewPaymentMethodId(v4() as PaymentMethod["id"])
                }
                liftSepaDebitData(true)
            }
        } catch (error) {
            liftSepaDebitData(false)

            // Kein Rethrow: Der Aufrufer (Formik-onSubmit im SEPA-Inline-Formular) wartet
            // das Promise nicht ab — ein Rethrow endete als unhandled rejection ohne jedes
            // User-Feedback. Stattdessen verständliche Meldung; das Formular bleibt offen
            // und liftSepaDebitData(false) hält den Submit-Button oben gesperrt.
            message.error(t("paymentMethodTable.saveError"))
        }
    }

    /** Badge/Icon-Rendering: Mollie-Zeilen erhalten ihr SVG-Icon, Standard-Zeilen den bisherigen Badge */
    const renderBadge = (record: CombinedTableData) => {
        if (record._source === "mollie") {
            const localIcon = MOLLIE_ICON_MAP[record.id]
            const label = record._mollieLabel || record.id
            return (
                <Tag>
                    {localIcon && (
                        <img
                            src={localIcon}
                            alt={label}
                            style={{ width: 18, height: 18, verticalAlign: "middle", marginRight: 6 }}
                        />
                    )}
                    {label}
                </Tag>
            )
        }
        return <PaymentMethodBadge type={getPaymentMethodType(record as PaymentMethodData)} />
    }

    const columns: Array<ColumnProps<CombinedTableData>> = [
        {
            title: "Verfahren",
            dataIndex: "id",
            render: (_text, record) => renderBadge(record),
        },
        {
            title: "Kontoinhaber",
            dataIndex: "sepaAccountholder",
            render: (text, record) => {
                // Mollie-Zeilen haben keinen Kontoinhaber
                if (record._source === "mollie") return null
                const paymentMethodType = getPaymentMethodType(record as PaymentMethodData)
                if (paymentMethodType === "sepaDebit") {
                    return record.sepaAccountholder
                }
                return null
            },
        },
        {
            title: "öffnen",
            key: "expand",
            width: 50,
            render: (_, record) => record._source !== "mollie" && record.sepaAccountholder !== null,
        },
    ]

    /**
     * Ermittelt welche Standard-Methoden durch Mollie-Methoden ersetzt werden.
     * Z.B. wenn Mollie "banktransfer" anbietet, wird die Standard-Methode "bankTransfer" ausgeblendet.
     */
    const getOverlappedStandardMethods = (): Set<PaymentMethodType> => {
        const overlapped = new Set<PaymentMethodType>()
        mollieMethods.forEach((mm) => {
            const standardEquivalent = MOLLIE_TO_STANDARD_OVERLAP[mm.id]
            if (standardEquivalent) {
                overlapped.add(standardEquivalent)
            }
        })
        return overlapped
    }

    const combinedTableData = (): Array<CombinedTableData> => {
        const combinedData: Array<CombinedTableData> = []
        const overlappedStandard = getOverlappedStandardMethods()

        // 1. Mollie-Methoden zuerst einfügen (erscheinen oben in der Liste)
        mollieMethods.forEach((mm) => {
            combinedData.push({
                id: mm.id,
                _source: "mollie",
                _mollieLabel: MOLLIE_METHOD_LABELS[mm.id] || mm.description || mm.id,
                // Alle Standard-Felder auf null damit getPaymentMethodType nicht fehlschlägt
                bank: null,
                bankTransfer: false,
                bic: null,
                cash: null,
                creditCardAccountholder: null,
                creditcardNumber: null,
                cvc: null,
                email: null,
                expirationDate: null,
                iban: null,
                paypalAccountholder: null,
                sepaAccountholder: null,
                validFrom: null,
                esr: null,
            })
        })

        // 2. Standard-Methoden des Bookers (gefiltert gegen Mollie-Überlappungen)
        const bookersMethodsStringArray: Array<PaymentMethodType> = []
        const bookersPaymentMethods = paymentMethods.map((paymentMethod) => {
            const methodType = getPaymentMethodType(paymentMethod)
            const companyMethod = availablePaymentMethodWithIds.find((method) => method.method === methodType)
            const companyMethodActive = companyMethod?.active
            bookersMethodsStringArray.push(methodType)
            return { ...paymentMethod, active: companyMethodActive, id: paymentMethod.id || v4(), _source: "standard" as RowSource }
        })

        const filteredBookersPaymentMethods = bookersPaymentMethods.filter((method) => {
            if (!method.active) return false
            // Standard-Methode ausblenden wenn Mollie diese abdeckt
            const methodType = getPaymentMethodType(method as PaymentMethodData)
            return !overlappedStandard.has(methodType)
        })

        combinedData.push(...filteredBookersPaymentMethods)

        // 3. Fehlende Company-Methoden hinzufügen (gefiltert gegen Mollie-Überlappungen)
        const companyPaymentMethods = availablePaymentMethodWithIds

        const missingMethods = companyPaymentMethods.filter((method) => {
            if (method.method === "id") return false
            if (overlappedStandard.has(method.method as PaymentMethodType)) return false
            return !bookersMethodsStringArray.includes(method.method)
        })

        missingMethods.forEach((method) => {
            const missingMethod: CombinedTableData = {
                bank: null,
                bankTransfer: false,
                bic: null,
                cash: true,
                creditCardAccountholder: null,
                creditcardNumber: null,
                cvc: null,
                email: null,
                expirationDate: null,
                iban: null,
                paypalAccountholder: null,
                sepaAccountholder: null,
                validFrom: null,
                esr: null,
                id: v4() as PaymentMethod["id"],
                _source: "standard",
            }

            switch (method.method) {
                case "esr":
                    missingMethod.id = method.id
                    missingMethod.esr = true
                    missingMethod.cash = null
                    break
                case "cash":
                    missingMethod.id = method.id
                    missingMethod.cash = true
                    break
                case "bankTransfer":
                    missingMethod.id = method.id
                    missingMethod.cash = null
                    missingMethod.bankTransfer = true
                    break
                case "sepaDebit":
                    missingMethod.id = method.id
                    missingMethod.cash = null
                    missingMethod.sepaAccountholder = ""
                    missingMethod.iban = ""
                    missingMethod.bank = ""
                    missingMethod.bic = ""
                    break
                default:
                    break
            }

            combinedData.push(missingMethod)
        })

        return combinedData
    }

    const paymentMethodIsMissing: boolean = selectedRowKeys[0] === undefined

    const warningStyle: React.CSSProperties = {
        border: "3px solid red",
        borderColor: red[2],
        borderRadius: "4px",
        padding: "4px",
        // width: "100%",
    }

    const style: React.CSSProperties = {
        // padding: "8px",
        width: "100%",
    }

    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: Array<Key> | Array<number>, newSelectedRows: Array<CombinedTableData>) => {
            const selectedRow = newSelectedRows[0]
            if (selectedRow && selectedRow._source === "mollie") {
                // Mollie-Methode gewählt → Standard-Auswahl aufheben
                setSelectedRowKeys(newSelectedRowKeys)
                if (onMollieMethodSelect) {
                    onMollieMethodSelect(selectedRow.id)
                }
            } else {
                // Standard-Methode gewählt → Mollie-Auswahl aufheben
                setSelectedRowKeys(newSelectedRowKeys)
                setSelectedPayment(selectedRow)
                if (onMollieMethodSelect) {
                    onMollieMethodSelect(null)
                }
                if (selectedRow) {
                    // B-82808: Sofort nach der Regel der gewählten Zeile sperren/freigeben, nicht erst
                    // nach dem Refetch. Die Platzhalter-Zeile „Lastschrift" (vier Leerstrings) sperrt
                    // damit schon im Fenster zwischen Klick und GetUpdatedBooker.
                    liftSepaDebitData(isPaymentMethodUsableForBooking(selectedRow))
                    const selectionSeq = ++selectionSeqRef.current
                    // Promise abfangen statt un-awaited verpuffen lassen: Schlägt UpdateBooker
                    // fehl, bekäme der User sonst keinerlei Feedback (unhandled rejection) und
                    // die UI zeigte eine Auswahl an, die nie gespeichert wurde.
                    createPaymentMethod(selectedRow)
                        .then(() => {
                            // Persistiert (oder bereits vorhanden): ab jetzt der Rollback-Anker.
                            committedSelectionRef.current = { keys: newSelectedRowKeys, rows: newSelectedRows }
                        })
                        .catch(() => {
                            message.error(t("paymentMethodTable.saveError"))
                            if (selectedMollieMethodRef.current || selectionSeq !== selectionSeqRef.current) {
                                // Der User hat inzwischen eine Mollie-Methode oder eine andere
                                // Standard-Methode gewählt — seine neue Auswahl gewinnt, der Rollback
                                // würde sie nur zerstören (Issue #116).
                                return
                            }
                            // Auf die zuletzt persistierte Auswahl zurück, nicht auf die Auswahl vor
                            // dem Klick — die kann selbst eine gescheiterte Methode sein.
                            const { keys: previousSelectedRowKeys, rows: previousSelectedRows } = committedSelectionRef.current
                            setSelectedRowKeys(previousSelectedRowKeys)
                            // Issue #113: Den Booking-State explizit mit zurückrollen. Ohne das behielt
                            // `bookingData.paymentMethodId` die nie gespeicherte neue ID, während die
                            // Tabelle die alte Zahlart zeigte.
                            const previousRow = previousSelectedRows[0]
                            setSelectedPayment({ id: previousRow?.id ?? "" })
                            // Submit-Gate nach derselben Regel wie im Erfolgsfall, jetzt für die
                            // wiederhergestellte Zeile. Ohne vorherige Auswahl bleibt es gesperrt.
                            liftSepaDebitData(previousRow ? isPaymentMethodUsableForBooking(previousRow) : false)
                        })
                }
            }
        },
    }

    // Tabelle direkt rendern. Eine im Rumpf definierte Komponente ist bei jedem Render ein neuer
    // Typ: React mountete Tabelle und SEPA-Formular neu und verwarf getippte Werte. Jedes Resize
    // (Handy-Tastatur, Adressleiste) leerte so Kontoinhaber und IBAN (Wassermäuse B-88485).
    return (
        <div className={css.root}>
            <Table<CombinedTableData>
                data-testid='payment-method-table'
                style={paymentMethodIsMissing && showAlert ? warningStyle : style}
                rowKey='id'
                showHeader={showHeader}
                columns={columns}
                dataSource={combinedTableData()}
                pagination={false}
                size='small'
                rowSelection={{ type: "radio", ...rowSelection }}
                bordered={false}
                expandable={{
                    expandedRowRender: (record) =>
                        record._source !== "mollie" && record.type === PaymentTransferType.SepaDebit && (
                            <div
                                style={{
                                    margin: 0,
                                }}
                            >
                                <SepaDebitPaymentMethodModal
                                    onSubmit={updateSepaDebitData}
                                    initialValues={{
                                        id: record.id ?? newPaymentMethodId,
                                        sepaAccountholder: record.sepaAccountholder,
                                        iban: record.iban,
                                        bank: record.bank,
                                        bic: record.bic,
                                    }}
                                    updatingBooker={updatingBooker}
                                />
                            </div>
                        ),
                    rowExpandable: (record) => record._source !== "mollie" && record.type === PaymentTransferType.SepaDebit,
                    expandIcon: () => null,
                    expandRowByClick: true,
                    expandIconColumnIndex: -1,
                    expandedRowKeys: selectedRowKeys,
                }}
            />
        </div>
    )
}

export default memo(PaymentMethodTable)
